import React from 'react';

export enum MODE {
  INFO = 'INFO',
  REPORT = 'REPORT',
}

export interface TextReport {
  ContentSequence?: Array<Record<string, unknown>>;
  ConceptNameCodeSequence?: {
    CodeValue?: string;
    CodeMeaning?: string;
    CodingSchemeDesignator?: string;
  };
  PatientID?: string;
  PatientName?: string | { Alphabetic: string };
  PatientSex?: string;
  PatientBirthDate?: string;
  StudyDate?: string;
  StudyDescription?: string;
  SeriesDescription?: string;
  Modality?: string;
  SOPClassUID?: string;
  SOPInstanceUID?: string;
  StudyInstanceUID?: string;
  SeriesInstanceUID?: string;
  StudyTime?: string;
  ContentDate?: string;
  AccessionNumber?: string;
  ReferringPhysicianName?: string;
  Manufacturer?: string;
  [key: string]: unknown;
}

interface ReportProperty {
  key: string;
  label: string;
  value: unknown;
  displayValue: string;
}

const KEYS_TO_DISPLAY: Array<{ key: string; label: string }> = [
  { key: 'PatientID', label: 'Patient ID' },
  { key: 'PatientName', label: 'Patient Name' },
  { key: 'PatientSex', label: 'Patient Sex' },
  { key: 'PatientBirthDate', label: 'Patient Birth Date' },
  { key: 'StudyDate', label: 'Study Date' },
  { key: 'StudyDescription', label: 'Study Description' },
  { key: 'SeriesDescription', label: 'Series Description' },
  { key: 'Modality', label: 'Modality' },
  { key: 'SOPClassUID', label: 'SOP Class UID' },
  { key: 'SOPInstanceUID', label: 'SOP Instance UID' },
  { key: 'StudyInstanceUID', label: 'Study Instance UID' },
  { key: 'SeriesInstanceUID', label: 'Series Instance UID' },
  { key: 'StudyTime', label: 'Study Time' },
  { key: 'ContentDate', label: 'Content Date' },
  { key: 'AccessionNumber', label: 'Accession Number' },
  { key: 'ReferringPhysicianName', label: 'Referring Physician' },
  { key: 'Manufacturer', label: 'Manufacturer' },
];

export function hasContentSequence(instance: TextReport | undefined): boolean {
  return !!(instance?.ContentSequence && instance.ContentSequence.length > 0);
}

function formatValue(value: unknown): string {
  if (!value) {
    return '';
  }

  if (typeof value === 'object') {
    const alphabetic = (value as { Alphabetic?: string }).Alphabetic;
    if (alphabetic) {
      return alphabetic;
    }
    try {
      return JSON.stringify(value);
    } catch (error) {
      return '[Complex Object]';
    }
  }

  return String(value);
}

function getAllReportProperties(instance: TextReport | undefined): ReportProperty[] {
  if (!instance) {
    return [];
  }

  const result: ReportProperty[] = [];

  if (instance.ConceptNameCodeSequence) {
    const { CodeValue, CodeMeaning, CodingSchemeDesignator } = instance.ConceptNameCodeSequence;
    result.push({
      key: 'CodeValue:CodeMeaning:CodingSchemeDesignator',
      label: 'Type of Report',
      value: `${CodeValue}:${CodeMeaning}:${CodingSchemeDesignator}`,
      displayValue: formatValue(`${CodeMeaning} (${CodeValue} - ${CodingSchemeDesignator})`),
    });
  }

  KEYS_TO_DISPLAY.forEach(({ key, label }) => {
    if (instance[key]) {
      result.push({
        key,
        label,
        value: instance[key],
        displayValue: formatValue(instance[key]),
      });
    }
  });

  Object.keys(instance).forEach(key => {
    if (!key.startsWith('1127') || !instance[key]) {
      return;
    }
    result.push({
      key,
      label: `Private Tag (${key})`,
      value: instance[key],
      displayValue: formatValue(instance[key]),
    });
  });

  return result;
}

export function TextReportView({ instance }: { instance: TextReport | undefined }) {
  const reportProperties = getAllReportProperties(instance);

  return (
    <div className="flex h-full w-full px-4 py-8 text-white">
      <div className="w-full overflow-auto text-sm">
        {reportProperties.length > 0 ? (
          <div className="grid grid-cols-1 gap-2">
            {reportProperties.map(({ key, label, displayValue }) => (
              <div
                key={key}
                className="grid grid-cols-2 border-b border-gray-700 py-1"
              >
                <span className="font-medium">{label}:</span>
                <span>{displayValue}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center">No report data available</div>
        )}
      </div>
    </div>
  );
}

export function ModeToggleButton({
  currentMode,
  onModeChange,
}: {
  currentMode: MODE;
  onModeChange: (mode: MODE) => void;
}) {
  return (
    <div className="border-primary-main flex h-4 rounded-lg border text-xs font-medium">
      <button
        type="button"
        className={`flex items-center rounded-l-lg px-2 transition-colors duration-200 ${
          currentMode === MODE.INFO
            ? 'bg-primary-main font-semibold text-white'
            : 'text-primary-main hover:bg-primary-main hover:text-white'
        }`}
        onClick={() => onModeChange(MODE.INFO)}
        aria-pressed={currentMode === MODE.INFO}
      >
        Info
      </button>
      <button
        type="button"
        className={`flex items-center rounded-r-lg px-2 transition-colors duration-200 ${
          currentMode === MODE.REPORT
            ? 'bg-primary-main font-semibold text-white'
            : 'text-primary-main hover:bg-primary-main hover:text-white'
        }`}
        onClick={() => onModeChange(MODE.REPORT)}
        aria-pressed={currentMode === MODE.REPORT}
      >
        Report
      </button>
    </div>
  );
}
